import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import queryString from 'query-string';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {faExclamationTriangle} from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';
import {faQuestionCircle} from '@fortawesome/pro-light-svg-icons/faQuestionCircle';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import Section, {SectionImage} from 'components/Elements/Section';
import HeaderVideo from 'components/Elements/HeaderVideo';
import CheckoutButton from 'components/CheckoutButton';
import CountdownTimer from 'components/Elements/CountdownTimer';
import NumberList, {NumberListItem} from 'components/Elements/NumberItem';
import {ModuleAccordionV1} from 'components/ModuleAccordion';
import Guarantee from 'components/Guarantee';
import {FaqListV1} from 'components/FaqList';
import {BonusAccordion} from 'components/BonusList';
import {SalesDisclaimer} from 'components/SalesCta';
import IconList, {IconListItem} from 'components/Elements/IconList';
import {TestimonialBubble} from 'components/Testimonial';
import ExitModal from 'components/ComponentLibrary/ExitModal';
import Footer from 'components/Elements/Footer';

import {modulesAd} from 'data/module.json';
import {faqAd} from 'data/faq.json';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = false,
  ctaOverride = null,
  ctaNoTimer = false,
  variant = null,
  version = undefined,
  ...props
}) {
  const {bucket, email, registrationId} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const {bucket: webinarBucket, report: webinarReport} = webinarSchedule;
  const {bucket: userBucket, events = []} = useUserState();
  const segment = webinarBucket ?? userBucket ?? '1';

  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {t} = qs.t ? qs : {};

  const isSalesDay = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime() < new Date().getTime();

  const salesFullPrice =
    (!email && !registrationId && !ctaOverride) ||
    (!ctaOverride && webinarSchedule.phase === 'after') ||
    ctaOverride === 'sales497';

  const specialTimer = version === 'salesSpecial' && salesSchedule.phase === 'during';
  const salesEnd = new Date(salesSchedule.salesEnd).getTime();
  const endOfSalesDay = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();

  const timerEnd = specialTimer ? salesEnd : endOfSalesDay;

  const [checkoutUrlControl, setcheckoutUrlControl] = useState(props.checkoutUrlControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setcheckoutUrlControl('v1');
          break;
      }
    }, 1281);
  }, []);
  const checkoutUrlSale = {control: 'hbc-special', v1: 'hbc-special-1'}[checkoutUrlControl];
  const checkoutUrlFull = {control: 'hbc', v1: 'hbc-1'}[checkoutUrlControl];

  // NOTE: waiting on the checkoutUrlControl/1281 test
  const [upsellUrlControl, setupsellUrlControl] = useState(props.upsellUrlControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setupsellUrlControl('v1');
          break;
      }
    }, 1282);
  }, []);
  const upsellUrlSale = {
    control: checkoutUrlSale /* TODO: update variable after 1281 test called */,
    v1: 'hbc-special-uhc'
  }[upsellUrlControl];
  const upsellUrlFull = {
    control: checkoutUrlFull /* TODO: update variable after 1281 test called */,
    v1: 'hbc-uhc'
  }[upsellUrlControl];

  const productId = 'C-HBC';
  const productSku = 'C-HBC';
  const productName = 'Healthy Brain';
  const productSlug = t || !salesFullPrice ? checkoutUrlSale : checkoutUrlFull;
  const productValue = t || !salesFullPrice ? 297 : 497;
  const productPaymentPlanValue = !salesFullPrice ? 109 : 179;
  const productFullPrice = 497;

  const videosControl =
    t || !salesFullPrice
      ? isSalesDay
        ? {
            1: 'https://www.youtube.com/watch?v=BNOZhnfbw1Q',
            2: 'https://www.youtube.com/watch?v=dvKVdYR1_9U'
          }[segment]
        : {
            1: 'https://www.youtube.com/watch?v=C2ByxM-vylo',
            2: 'https://www.youtube.com/watch?v=cD4HgaVWBJE'
          }[segment]
      : 'https://www.youtube.com/watch?v=RJJOTmVtipw';

  useEffect(() => {
    track('Product Viewed', {
      product_id: productId,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  }, [salesFullPrice]);

  function SalesCta({headline, btnText = 'Buy Now', className = undefined, noImage = false, trees = true}) {
    return (
      <div className={classNames(`cta-sales`, className)}>
        {headline && <div className="cta-headline text-center text-white">{headline}</div>}
        <div
          className={classNames(
            `offer-box`,
            `offer-box-shadow`,
            `px-3`,
            `py-4`,
            `px-sm-4`,
            `px-md-5`,
            `py-md-5`,
            `text-center`,
            `background-white`,
            `rounded`
          )}
        >
          {!noImage && (
            <Row className="justify-content-center mb-4">
              <Col xs="10" md="8" xl="7">
                <img
                  src="https://cdn.foodrevolution.org/hbc/hbc-product_image.png"
                  width="1200"
                  height="500"
                  alt="Healthy Brain product graphic"
                />
              </Col>
            </Row>
          )}
          <h5 className="mt-0">
            Get instant access — even <br className="d-none d-md-block d-lg-none" />
            at 2 am – guaranteed!
          </h5>
          {!salesFullPrice ? (
            <>
              <h3 className="text-green mt-0 mb-2">
                All For Just: <del className="text-medium-gray">${productFullPrice}</del>
              </h3>
              <h2 className="text-green mt-0">Right Now Only: ${productValue}</h2>
            </>
          ) : (
            <>
              <h2 className="text-h1 text-green mt-0 mb-2">Right Now ${productFullPrice}</h2>
            </>
          )}
          {!salesFullPrice && !ctaNoTimer ? (
            <div className="my-3">
              <h5 className="section-heading mb-2">Your discount expires in:</h5>
              <div className="countdown-dark">
                <CountdownTimer time={timerEnd} />
              </div>
            </div>
          ) : null}
          <CheckoutButton className="offer-box-button" sku={productSku} price={productValue} slug={productSlug}>
            <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {btnText}
          </CheckoutButton>
          <SalesDisclaimer />
          <p className="mt-4 mb-1">Prefer a 3-month payment plan? We’ve got you covered!</p>
          <CheckoutButton
            color="link"
            className="btn-link-checkout"
            sku={productSku}
            price={productPaymentPlanValue}
            slug={productSlug}
            queryParams={{opt: 1}}
          >
            Join now for just ${productPaymentPlanValue}/month!
          </CheckoutButton>
        </div>

        {trees && (
          <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
            <Col xs="11" lg="8" className="mb-4 mb-lg-0">
              <h6 className="section-only-heading text-center text-lg-left text-400">
                For every new order, we make a donation to Trees for the Future, enabling them to plant another organic
                fruit or nut tree in a low-income community.
              </h6>
            </Col>
            <Col xs="11" lg="3" className="text-center">
              <img
                alt="Trees for the Future logo"
                src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                width={1080}
                height={442}
                style={{maxHeight: '100px', width: 'auto'}}
              />
            </Col>
          </Row>
        )}
      </div>
    );
  }

  //useBalanceText();
  useBeacon();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta
        title="Join Healthy Brain: Eat, move, sleep, think, and play for lifelong brain health!"
        description="Learn how you can protect your brain and boost its performance at any age with FRN CEO, Ocean Robbins, and the fabulous The Brain Docs in Healthy Brain."
        url="https://brain.foodrevolution.org/join/"
        ogImage="https://cdn.foodrevolution.org/hbc/og/hbc-open-graph-20200903-evergreen-sales-fbt.jpg"
      />
      <div className="page-content">
        <Section id="hbc-header" className="section-angle-first" innerClass="text-white" color="black">
          <header className="hbc-header-join header-wrap text-center">
            <Container>
              <Row className="mb-2">
                <Col className="d-flex align-items-center flex-column flex-lg-row justify-content-lg-center">
                  <div className="mb-2 mb-lg-0 mr-lg-2">Food Revolution Network Presents...</div>
                  <img className="img-fluid logo-hbc" src="https://cdn.foodrevolution.org/hbc/hbc-logo-white.svg" />
                </Col>
              </Row>
            </Container>
          </header>

          <section className="hbc-header-cta text-center mt-4">
            <Container>
              <Row>
                <Col>
                  <p className="text-base balance-text">
                    Hey, I’m Ocean, and today I want to ask you a simple question...
                  </p>
                  <h1 className="mt-3">
                    {variant === 'ad' ? (
                      <>Do you want to keep your brain healthy for years to come?</>
                    ) : (
                      <>
                        {
                          {
                            1: <>Do you want to avoid dementia and Alzheimer’s disease?</>,
                            2: <>Do you want to boost your brain power and get more done every day?</>
                          }[segment]
                        }
                      </>
                    )}
                  </h1>
                  <p className="balance-text">
                    <b>We’ve been taught that cognitive decline is inevitable. Watch this video to learn the truth:</b>
                  </p>
                </Col>
              </Row>
              <Row className="d-flex justify-content-center">
                <Col lg="10" className="video-col px-0 px-sm-3">
                  <HeaderVideo
                    bgColor="black"
                    className="text-white"
                    sku={productSku}
                    price={productValue}
                    slug={productSlug}
                    url={videosControl}
                    sales
                    heading="Eat, Move, Sleep, Think, & Play for Lifelong Brain Health"
                    btnText="Join Healthy Brain Now"
                    noFixed={noFixedVideo}
                    label="HBC - Sales Video - Join"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <h3 className="mb-2">
                    {variant === 'ad' ? (
                      <>Keep your mind and your memories sharp</>
                    ) : (
                      <>
                        {
                          {
                            1: <>Slash Your Risk of Memory Loss</>,
                            2: <>Boost Your Brain Power Dramatically</>
                          }[segment]
                        }
                      </>
                    )}
                  </h3>
                  <h5 className="m-0">using delicious food and all-natural solutions that don’t cost a dime.</h5>
                </Col>
              </Row>
              <Row className="d-flex justify-content-center my-4">
                <Col xs="10" md="8" lg="6" xl="5">
                  <img className="img-fluid" src={require('static/portrait-thumbs.png')} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <h4 className="section-heading">
                    {variant === 'ad' ? (
                      <>
                        With Dean and Ayesha Sherzai, MD and Your Host, Ocean Robbins (CEO of Food Revolution Network)
                      </>
                    ) : (
                      <>
                        With Dean and Ayesha Sherzai, MD (Authors of <i>The Alzheimer’s Solution</i>) and Your Host,
                        Ocean Robbins (CEO of Food Revolution Network)
                      </>
                    )}
                  </h4>

                  <p className="mb-4">Get immediate access to the breakthrough program now.</p>
                  {t || !salesFullPrice ? (
                    <>
                      <h2 className="section-heading mb-3">
                        Right Now Just <del>${productFullPrice}</del> ${productValue}
                      </h2>
                      {!salesFullPrice && !ctaNoTimer ? (
                        <div className="my-3">
                          <h5 className="section-heading mb-2">Your discount expires in:</h5>
                          <div className="countdown-light">
                            <CountdownTimer time={timerEnd} />
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <h2 className="section-heading">Right Now ${productFullPrice}</h2>
                    </>
                  )}
                  <CheckoutButton className="mb-4" sku={productSku} price={productValue} slug={productSlug}>
                    Join Healthy Brain Now
                  </CheckoutButton>
                  <SalesDisclaimer iconColor="white" />
                </Col>
              </Row>
            </Container>
          </section>
        </Section>

        <SectionImage
          id="hbc-if-you-want"
          color="white"
          imgPosition="right"
          imgUrl={require('static/backgrounds/women-smiling-square.jpg')}
        >
          <h2 className="section-heading">Would you like to:</h2>
          <IconList>
            {
              {
                1: (
                  <>
                    <IconListItem>
                      {variant === 'ad' ? (
                        <>
                          <b>Say no to a future</b> of depending on caregivers to meet your basic needs
                        </>
                      ) : (
                        <>
                          <b>Say no to a future</b> of depending on caregivers to meet your basic needs
                        </>
                      )}
                    </IconListItem>
                    <IconListItem>
                      Replace fear of decreased brain function with <b>positive and scientifically proven action</b>
                    </IconListItem>
                    {variant === 'ad' ? null : (
                      <IconListItem>
                        Help to protect your family from the devastating financial burden that typically comes with a{' '}
                        <b>dementia diagnosis</b>
                      </IconListItem>
                    )}
                    <IconListItem>
                      {variant === 'ad' ? (
                        <>
                          Say goodbye to fad diets and gimmicks — and{' '}
                          <b>say hello to what the longest running scientific studies</b> say about the{' '}
                          <b>best food for a long life with a healthy brain</b>
                        </>
                      ) : (
                        <>
                          Say goodbye to fad diets and gimmicks — and{' '}
                          <b>discover what the longest running scientific studies</b> say about the{' '}
                          <b>best food for a long, dementia-free life</b>
                        </>
                      )}
                    </IconListItem>
                  </>
                ),
                2: (
                  <>
                    <IconListItem>
                      Say goodbye to the hype of fad diets and gimmicks — and{' '}
                      <b>discover what the longest running scientific studies</b> say about the{' '}
                      <b>best boosting IQ food</b>
                    </IconListItem>
                  </>
                )
              }[segment]
            }
            <IconListItem>
              {variant === 'ad' ? (
                <>
                  <b>Say goodbye to brain fog</b> once and for all — even if it’s been a lifelong challenge
                </>
              ) : (
                <>
                  <b>End brain fog</b> once and for all — even if you’ve been dealing with it for years
                </>
              )}
            </IconListItem>
            {variant === 'ad' ? null : (
              <IconListItem>
                Prevent or <b>reverse “seemingly harmless” signs</b> of mental decline
              </IconListItem>
            )}
            <IconListItem>
              {variant === 'ad' ? (
                <>
                  <b>Improve memory now,</b> and add up to 7.5 years of sharp cognition
                </>
              ) : (
                <>
                  <b>Improve your memory now,</b> and add up to 7.5 years of sharp cognition to your life
                </>
              )}
            </IconListItem>
            <IconListItem>
              {variant === 'ad' ? (
                <>
                  <b>Fall asleep faster, sleep deeper,</b> and wake up refreshed
                </>
              ) : (
                <>
                  <b>Fall asleep faster, sleep deeper,</b> and wake up more refreshed than you ever thought possible
                </>
              )}
            </IconListItem>
            <IconListItem>Delight your taste buds with brain-boosting new recipes</IconListItem>
            <IconListItem>
              {variant === 'ad' ? (
                <>
                  Best of all — <b>succeed at lasting habit change</b> thanks to the latest neuroscience
                </>
              ) : (
                <>
                  Best of all — discover how to finally <b>succeed at lasting habit change</b> by leveraging the latest
                  neuroscience
                </>
              )}
            </IconListItem>
          </IconList>
          <h5 className="text-700 mb-0">
            <i>If that sounds good, you’re in the right place!</i>
          </h5>
        </SectionImage>

        <SectionImage
          id="hbc-familiar"
          color="light-gray"
          imgPosition="left"
          imgUrl={require('static/backgrounds/woman-forgot-square.jpg')}
          title={
            <>
              <p className="balance-text">
                <b>If you’re like many people, you may experience the occasional moment of forgetfulness.</b> As a
                matter of fact...
              </p>
              <h3 className="m-0">
                It’s surprisingly common how many people nervously joke about experiencing cognitive decline starting as
                early as their 40s.
              </h3>
            </>
          }
          titleClass="text-lg-center mb-lg-4 mb-xl-5"
        >
          <h5 className="section-heading">Are these moments harmless… or are they warning signs from your brain?</h5>
          <p>Any of these sound familiar?</p>
          <IconList iconColor="orange">
            <IconListItem icon={faExclamationTriangle}>
              {variant === 'ad' ? (
                <>“Ha, I left my keys in the refrigerator. I must be having a ‘senior moment.’”</>
              ) : (
                <>Ha, I left my keys in the refrigerator. I must be getting dementia.</>
              )}
            </IconListItem>
            <IconListItem icon={faExclamationTriangle}>
              {variant === 'ad' ? (
                <>“Where are my glasses? Oh, ha, they’re on top of my head.”</>
              ) : (
                <>Where are my glasses? Oh, ha, they’re on top of my head.</>
              )}
            </IconListItem>
            <IconListItem icon={faExclamationTriangle}>
              {variant === 'ad' ? <>“I must be losing it.”</> : <>I must be losing it.</>}
            </IconListItem>
            <IconListItem icon={faExclamationTriangle}>
              {variant === 'ad' ? (
                <>“I just have part-timers. Part of the time, I’m all here; the other times, I’m not all there.”</>
              ) : (
                <>I just have part-timers. Part of the time, I’m all here; the other times, I’m not all there.</>
              )}
            </IconListItem>
          </IconList>
          <p>
            We often use humor to hide our fear. It feels easier to shrug these little moments off with a chuckle and a
            smile. “Nothing’s wrong,” we tell ourselves.
          </p>
          <p>
            <b>But how do we know if we might be ignoring a distress signal from our brain?</b>
          </p>
        </SectionImage>

        <SectionImage
          id="hbc-moments"
          color="white"
          imgPosition="right"
          imgUrl={require('static/backgrounds/woman-rotary-phone-square.jpg')}
        >
          <p>
            How do you <b>know when these moments</b> —
          </p>
          <IconList iconColor="purple">
            <IconListItem icon={faQuestionCircle}>
              {variant === 'ad' ? <>“Why can’t I focus like I used to?”</> : <>Why can’t I focus like I used to?</>}
            </IconListItem>
            <IconListItem icon={faQuestionCircle}>
              {variant === 'ad' ? (
                <>“Why did I forget to stop at the grocery store?”</>
              ) : (
                <>Why did I forget to stop at the grocery store?</>
              )}
            </IconListItem>
            <IconListItem icon={faQuestionCircle}>
              {variant === 'ad' ? (
                <>“How come I have trouble remembering words and people’s names?”</>
              ) : (
                <>How come I have trouble remembering words and people’s names?</>
              )}
            </IconListItem>
            <IconListItem icon={faQuestionCircle}>
              {variant === 'ad' ? (
                <>“Wait. Why did I come into this room?”</>
              ) : (
                <>Wait. Why did I come into this room?</>
              )}
            </IconListItem>
          </IconList>
          <p>
            {variant === 'ad' ? (
              <>
                — are actually <b>foreshadowing something more worrisome?</b>
              </>
            ) : (
              <>
                — are actually <b>foreshadowing oncoming dementia?</b>
              </>
            )}
          </p>
          {variant === 'ad' ? (
            <>
              <h4 className="mt-4">The truth is mental decline doesn’t come from out of nowhere.</h4>
              <h5 className="mt-0 text-400">
                In fact, the groundwork is laid years, even decades, before the onset of symptoms.
              </h5>
              <h5 className="mt-0 text-400">
                In fact, these seemingly harmless signs might be early red flags — which can start decades before the
                onset of full blown symptoms.
              </h5>
            </>
          ) : (
            <>
              {
                {
                  1: (
                    <>
                      <h4 className="mt-4">The truth is Alzheimer’s doesn’t come out of nowhere.</h4>
                      <h5 className="mt-0 text-400">
                        In fact, the groundwork for dementia is laid years, even decades, before the onset of symptoms.
                      </h5>
                    </>
                  ),
                  2: (
                    <>
                      <h4 className="mt-4">The truth is a sluggish brain doesn’t come out of nowhere.</h4>
                      <h5 className="mt-0 text-400">
                        Little things, like forgetting names and brain fog slowly set in. But left unchecked, these
                        annoyances will get worse.
                      </h5>
                      <h5 className="mt-0 text-400">
                        In fact, these seemingly harmless signs might be early red flags for dementia — which can start
                        decades before the onset of full blown symptoms.
                      </h5>
                    </>
                  )
                }[segment]
              }
            </>
          )}
          <h4 className="mt-0">
            The time to take action is <i>now.</i>
          </h4>
        </SectionImage>

        <Section id="hbc-worldwide-1" color="light-gray">
          <Container>
            <Row className="justify-content-center">
              <Col lg="10">
                <h3 className="section-heading">
                  <i>
                    {
                      {
                        1: <>Is Alzheimer’s more deadly than we thought?</>,
                        2: <>Should I be worried about Alzheimer’s?</>
                      }[segment]
                    }
                  </i>
                </h3>
                <p>Everyone wants to feel mentally sharp, productive, and have a mind like a steel trap.</p>
                <p>
                  But the truth is that brain health is one of the most often-ignored areas of health. It’s common to
                  see people avoid certain foods for their heart health, but few people know what it takes to keep the
                  brain healthy in the long term.
                </p>
                <p>That’s because much of what we know about taking care of the brain is so new.</p>
                <p>Yes, Alzheimer’s is on the rise.</p>
                <p>
                  And the early warning signs like forgetfulness and poor sleep are often glossed over as “normal.” But
                  please don’t ignore them.
                </p>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="hbc-worldwide-2"
          color="light-gray"
          imgPosition="left"
          imgUrl={require('static/backgrounds/woman-elderly-window-square-mirror.jpg')}
          imgBgPosition="center-top"
        >
          <p>The fact is that right now, worldwide,</p>
          <h2 className="mt-4">
            {variant === 'ad' ? (
              <>
                50 million people are living with brain diseases.<sup>(1)</sup>
              </>
            ) : (
              <>
                50 million people are living with Alzheimer’s disease and other dementias.
                <sup>(1)</sup>
              </>
            )}
          </h2>
          {variant === 'ad' ? (
            <>
              <p className="balance-text">While nearly every chronic health issue you can think of is in decline…</p>
              <p className="balance-text">
                <b>
                  These deaths <i>have increased by nearly 87%</i> in the last decade.<sup>(3)</sup>
                </b>
              </p>
            </>
          ) : (
            <>
              <p>
                <b>
                  Officially, Alzheimer’s disease is the sixth-leading cause of death in the United States.
                  <sup>(2)</sup>
                </b>
              </p>
              <p>But many researchers believe that Alzheimer’s is grossly underreported on death certificates.</p>
              <p className="balance-text">
                <b>
                  In fact, it might be America’s third-most deadly disease, <i>behind only heart disease and cancer.</i>
                  <sup>(2)</sup>
                </b>
              </p>
              <p>
                While nearly every chronic disease you can think of — cardiovascular disease, cancer, stroke, HIV — is
                in decline…
              </p>
              <p className="balance-text">
                <b>
                  Deaths due to Alzheimer’s <i>have increased by nearly 87%</i> in the last decade.
                  <sup>(3)</sup>
                </b>
              </p>
            </>
          )}
        </SectionImage>

        <SectionImage
          id="hbc-solutions"
          color="white"
          imgPosition="right"
          imgUrl={require('static/backgrounds/woman-boy-square.jpg')}
        >
          <p>
            <b>And, the next decade could be even worse.</b>
          </p>
          <h3 className="my-3">
            Tragically, most people don’t give their brain the attention it deserves — until it’s too late.
          </h3>
          <p>Here’s an important stat you need to know:</p>
          <h4 className="mt-3">
            {variant === 'ad' ? (
              <>
                Right now, people over the age of 85 have a 50/50 chance of developing severe cognitive decline.
                <sup>(4)</sup>
              </>
            ) : (
              <>
                Right now, people over the age of 85 have a 50/50 chance of developing dementia.<sup>(4)</sup>
              </>
            )}
          </h4>
          <p>
            {variant === 'ad' ? (
              <>This means that almost every single family will be affected by a loved one with cognitive decline.</>
            ) : (
              <>
                Many of us can reasonably expect to live beyond 85, especially if treatments for major diseases continue
                to improve. This means that almost every single family will be affected by a loved one with dementia.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>The emotional cost is immeasurable.</>
            ) : (
              <>The emotional cost of the disease is immeasurable.</>
            )}
          </p>
          {variant === 'ad' ? (
            <>
              <p>And the financial burden can be overwhelming.</p>
              <p>
                Unprepared families can be <i>devastated</i> by the healthcare costs.
              </p>
            </>
          ) : (
            <>
              <p className="balance-text">
                And the financial cost is absolutely overwhelming. Unprepared families can be <i>devastated</i> by the
                healthcare costs of this disease — years of doctors, medications, and round-the-clock care is expensive,
                even with insurance.
              </p>
            </>
          )}
          <p>
            {variant === 'ad' ? (
              <>
                And rates of new cases are expected to triple in the next generation!<sup>(4, 5)</sup>
              </>
            ) : (
              <>
                In fact, Alzheimer’s is the #1 most costly disease in America. And rates of Alzheimer’s are expected to
                triple in the next generation!<sup>(4, 5)</sup>
              </>
            )}
          </p>
          <p className="balance-text">
            <b>
              {variant === 'ad' ? (
                <>But we’re not here to bring you down. Keep reading to find out about SOLUTIONS.</>
              ) : (
                <>But I’m not here to depress you. Keep reading to find out about SOLUTIONS.</>
              )}
            </b>
          </p>
        </SectionImage>

        <Section id="hbc-good-news" color="black" innerClass="text-white text-center">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h4 className="mt-0 mb-4">
                  In the last 15 years, some of the world’s most advanced doctors and scientists have discovered some
                  remarkably GOOD News:
                </h4>
                <h5 className="text-400 mt-0 mb-4">
                  {variant === 'ad' ? (
                    <>It IS Possible to Keep Your Brain Healthy Now & In The Future</>
                  ) : (
                    <>
                      {
                        {
                          1: <>It IS Possible to Reduce Your Risk of Alzheimer’s Disease by over 50%.</>,
                          2: (
                            <>
                              It IS Possible to Dramatically Boost Your Focus, Alertness, Energy (AND Decrease Risk of
                              Dementia)... without coffee, naps, or outrageously expensive “superfoods.”
                            </>
                          )
                        }[segment]
                      }
                    </>
                  )}
                </h5>
                <h5 className="m-0">
                  And the best part is you can do it with astonishingly simple diet and lifestyle changes that you’ll
                  LOVE.
                </h5>
              </Col>
            </Row>
            <Row>
              <Col>
                <h2 className="text-white text-center mt-5 mb-3">
                  “Your everyday choices, <i>not your genetics,</i> determine your cognitive fate.”
                </h2>
                <h4 className="text-center text-white m-0">— Drs. Dean & Ayesha Sherzai</h4>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="hbc-introducing" color="white">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h1 className="section-heading text-center">Introducing…</h1>
                <Row className="d-flex justify-content-center mb-3">
                  <Col xs="12" lg="10">
                    <img
                      src="https://cdn.foodrevolution.org/hbc/hbc-product_image.png"
                      width="1200"
                      height="500"
                      alt="Healthy Brain graphic"
                    />
                  </Col>
                </Row>
                <h2 className="text-center mt-0 mb-2">
                  <i>Healthy Brain:</i>
                </h2>
                <h4 className="text-center mt-0">
                  <i>Eat, Move, Sleep, Think, & Play for Lifelong Brain Health</i>
                </h4>
                <hr className="my-5" />
                <TestimonialBubble id="hbc-jim" className="mb-5" />
                <TestimonialBubble id={variant === 'ad' ? 'hbc-sara-p-ad' : 'hbc-sara-p'} className="mb-5" />
                <TestimonialBubble id="hbc-colleen-s" className="mb-5" />
                <TestimonialBubble id="hbc-joyce-c" className="mb-5" />
                <TestimonialBubble id={variant === 'ad' ? 'hbc-susan-f-ad' : 'hbc-susan-f'} />
                <p className="text-small text-center balance-text mt-5">
                  <i>
                    These stories are from people who’ve found success with our programs. Each of them is unique. And so
                    are you! Your experiences will vary, of course. And no, we never offer compensation for these
                    statements!
                  </i>
                </p>
                <hr className="my-5" />
                <h4>
                  This 6-module course gives you brain health keys distilled directly from two of the world’s most savvy
                  brain experts.
                </h4>
                <p>
                  Built upon the life-changing <b>NEURO Plan</b> — pioneered by the brilliant neurologists, Drs. Dean
                  and Ayesha Sherzai, MD (affectionately known as The Brain Docs) — Healthy Brain gives you breakthrough
                  solutions backed by the longest running and most credible scientific studies.
                </p>
                <p>
                  NEURO is a comprehensive and holistic plan that encompasses all the key areas for optimal brain
                  health: <b className="text-underline">N</b>utrition, <b className="text-underline">E</b>
                  xercise, <b className="text-underline">U</b>
                  nwind, <b className="text-underline">R</b>estorative sleep, and <b className="text-underline">O</b>
                  ptimize.
                </p>

                <h4>
                  <i>Healthy Brain</i> is for you if you want to...
                </h4>
                <NumberList>
                  <NumberListItem
                    number="1"
                    title={
                      variant === 'ad' ? (
                        <>Boost brain power...</>
                      ) : (
                        <>
                          Boost your brain power... <i>immediately.</i>
                        </>
                      )
                    }
                  >
                    {variant === 'ad' ? (
                      <>
                        Start experiencing positive results right away as The Brain Docs shows you exactly how you can
                        build new neural pathways for rapid brain performance!
                      </>
                    ) : (
                      <>
                        Start experiencing positive results right away as The Brain Docs shows you exactly how to build
                        new neural pathways for rapid brain performance — in as little as a few days or weeks!
                      </>
                    )}
                  </NumberListItem>

                  <NumberListItem
                    number="2"
                    title={
                      variant === 'ad' ? (
                        <>Finally master habits (instead of being a victim to them).</>
                      ) : (
                        <>Finally master your habits (instead of being a victim to them).</>
                      )
                    }
                  >
                    Discover how you may be using a hopeless tactic when it comes to changing your habits — and what
                    actually works (you won’t believe how simple and smart the Sherzai formula is!).
                  </NumberListItem>

                  <NumberListItem number="3" title="Reduce high-stress levels — without overhauling your entire life.">
                    {variant === 'ad' ? (
                      <>
                        Learn the science of stress, feel-good ways to reduce it, and how inviting more fun into your
                        life can add up to 7.5 additional years of healthy brain function.
                      </>
                    ) : (
                      <>
                        Learn the science of stress, feel-good ways to reduce it, and how inviting more fun into your
                        life can give you up to 7.5 additional years of healthy brain function.
                      </>
                    )}
                  </NumberListItem>

                  <NumberListItem number="4" title="Enjoy deeper sleep and more energy during the day.">
                    {variant === 'ad' ? (
                      <>
                        After a lifetime of tossing and turning… finally get that elusive great night’s sleep and watch
                        how your moods can improve and productivity can skyrocket. Wake up well-rested, full of energy,
                        and ready to take on the day. The Brain Docs will show you how!
                      </>
                    ) : (
                      <>
                        After a lifetime of tossing and turning… finally get that elusive great night’s sleep and watch
                        your moods improve and productivity skyrocket. Wake up well-rested, full of energy, and ready to
                        take on the day. The Brain Docs will show you how!
                      </>
                    )}
                  </NumberListItem>

                  <NumberListItem number="5" title="Keep your memory sharp for years to come.">
                    {variant === 'ad' ? (
                      <>
                        Discover how to take action now for the early symptoms of cognitive decline. The sooner you
                        learn these brain tips, the more stunning and long-lasting the results, so <b>start now!</b>
                      </>
                    ) : (
                      <>
                        Discover how to take action <i>now</i> to prevent and even reverse early symptoms of cognitive
                        decline. The sooner you learn these brain hacks, the more stunning and long-lasting your
                        results, so <b>start now!</b>
                      </>
                    )}
                  </NumberListItem>

                  <NumberListItem number="6" title="Fall madly in love with brain-healthy foods.">
                    {variant === 'ad' ? (
                      <>
                        <p>
                          The latest scientific discoveries show that what’s for breakfast, lunch, and dinner can
                          determine if you spend your golden years lucid… or under careful watch.
                        </p>
                        <p className="balance-text mb-0">
                          Gain the food wisdom that can only be provided by a neuroscientist who’s <i>also</i> a
                          professional chef! Dr. Ayesha Sherzai will share some of her <i>favorite</i> recipes and
                          demonstrate how YOU can make them for dinner <i>tonight.</i>
                        </p>
                      </>
                    ) : (
                      <p className="balance-text">
                        The latest scientific discoveries show that what’s for breakfast, lunch, and dinner can
                        determine if you spend your golden years lucid… or under careful watch. Gain the food wisdom
                        that can only be provided by a neuroscientist who’s <i>also</i> a professional chef. Dr. Ayesha
                        Sherzai will share some of her <i>favorite</i> recipes and demonstrate how YOU can make them for
                        dinner <i>tonight.</i>
                      </p>
                    )}
                  </NumberListItem>
                </NumberList>

                <h5>
                  All WITHOUT spending a fortune on pills, supplements, pricey brain food fads, or special equipment.
                </h5>
                <p className="balance-text">
                  In fact, most of what the science points to <i>won’t cost a thing.</i>
                </p>
                <p className="balance-text">
                  This is the <i>perfect</i> course for people who value self-reliance and don’t want to spend an arm
                  and a leg to get breakthrough results.
                </p>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="hbc-steve"
          color="light-gray"
          imgPosition="left"
          imgUrl={require('static/backgrounds/man-running-square.jpg')}
          imgBgPosition="center-top"
        >
          <h4 className="section-heading">Does this work? Here’s Steve’s Story.</h4>
          <p>
            {variant === 'ad' ? (
              <>
                At 62 years old — fairly young by the standards of most brain health patients — Steve was experiencing{' '}
                <b>a constant sense of mental fogginess and lack of clarity.</b>
              </>
            ) : (
              <>
                At 62 years old — fairly young by the standards of most Alzheimer’s or dementia patients — Steve was
                experiencing <b>a constant sense of mental fogginess and lack of clarity.</b>
              </>
            )}
          </p>
          <p>
            He said it was like he wasn’t even there — like he was having a constant out-of-body experience: a dreamlike
            state, <b>almost a nonexistence.</b>
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Steve was struggling to maintain a thought process from beginning to end, and so was hardly able to
                focus long enough to have coherent thoughts, let alone communicate them.{' '}
                <b>He exhibited enough cognitive decline that his doctors gave him a diagnosis.</b>
              </>
            ) : (
              <>
                Steve was struggling to maintain a thought process from beginning to end, and so was hardly able to
                focus long enough to have coherent thoughts, let alone communicate them.{' '}
                <b>He exhibited enough cognitive decline to be diagnosed with early-stage dementia.</b>
              </>
            )}
          </p>
          <p>
            One thing that stood out about Steve was that he used to be an athlete. He had run his whole life until age
            56 when he stopped because of an injury. Even though his injury healed, he never ran again. He became a
            couch potato.
          </p>
          <p>
            Steve hadn’t gained weight, but his muscles were weak, and he became light-headed when getting up out of a
            chair. He had lost almost all motivation and zeal for life.
          </p>
          <p>
            He could feel his brain starting to turn to mush, and he was worried about what would happen if he didn’t do
            something about his situation.
          </p>
          <p>
            Steve didn’t want to continue on this path. He wanted to make sure the next decades of his life were
            enriching and full of lasting experiences with his loved ones. And his family was desperate to hold on to
            their husband, father, uncle, and grandfather as long as possible.
          </p>
          <p>
            <b>Was there anything he could do?</b>
          </p>
        </SectionImage>

        <SectionImage
          id="hbc-steve-two"
          color="white"
          imgPosition="right"
          imgUrl={require('static/backgrounds/man-sunset-square-mirror.jpg')}
          title={
            <>
              <p>Did this stuff work for him?</p>
              <p className="mb-4">I’m glad you asked. Remember…</p>
              <h3 className="mt-0">
                {variant === 'ad' ? (
                  <>Steve had already been diagnosed at another clinic.</>
                ) : (
                  <>Steve had already been diagnosed with Alzheimer's at another clinic.</>
                )}
              </h3>
            </>
          }
          titleClass="mb-0"
        >
          <p>
            {variant === 'ad' ? (
              <>
                But, when The Brain Docs examined Steve, they realized that <b>it was something else.</b>
              </>
            ) : (
              <>
                But, when The Brain Docs examined Steve, they realized that{' '}
                <b>he didn’t have Alzheimer's; it was something else.</b>
              </>
            )}
          </p>
          <p>However, it wasn’t all great news.</p>
          <p>
            He did have <b>short-term memory loss, and all aspects of cognition were involved:</b> attention, focus,
            recall, and executive function.
          </p>
          <p>
            {variant === 'ad' ? (
              <>What an emotional roller coaster for Steve.</>
            ) : (
              <>
                What an emotional roller coaster for Steve. To first get an Alzheimer’s diagnosis, to later learn that
                it wasn’t Alzheimer’s, but then to immediately discover it was something else that might be just as bad.
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                It would have been so easy for Steve to lose hope and give up. Thankfully, he wasn’t with ordinary
                doctors. He was with The Brain Docs, who could very well be the world’s top experts on the best diet and
                lifestyle for brain health.
              </>
            ) : (
              <>
                It would have been so easy for Steve to lose hope and give up. Thankfully, he wasn’t with ordinary
                doctors. He was with The Brain Docs, who could very well be the world’s top experts on how to fight
                Alzheimer’s and boost brain health with diet and lifestyle.
              </>
            )}
          </p>
          <p>
            <b>He committed to their program.</b>
          </p>
          <p className="balance-text">
            Shortly after implementing the pillars of NEURO — which the entire <i>Healthy Brain</i> course is built upon
            — Steve was up and moving, he was walking more with recovered balance.
          </p>
          <p>His brain fog completely cleared.</p>
          {variant === 'ad' ? null : <p>His blood pressure improved.</p>}
          <p>His zeal for life returned.</p>
          <h3 className="mt-4">
            He got his brain back.
            <br />
            He got his <i>life</i> back.
          </h3>
          <h4 className="mt-0 text-400">
            And his wife, children, and grandkids got <i>Steve</i> back.
          </h4>
        </SectionImage>

        <Section
          id="hbc-lifework"
          className="background-image-wood-panel background-image-left-bottom background-cover background-fixed"
          color="white"
          angle="none"
          innerClass="section-inner-noangle-offset"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-white box-shadow-black-25 px-3 py-4 px-lg-5 py-lg-5 my-3">
                  <Row className="d-flex align-items-center justify-content-center mb-lg-4">
                    <Col xs="12" sm="8" lg="4" xl="3" className="order-lg-2 text-center mb-4 mb-lg-0">
                      <img
                        className="img-ocean-note rounded-circle"
                        src={require('static/speaker/ocean-robbins.jpg')}
                      />
                      <p className="text-small mt-2 mb-0">
                        <i>Ocean Robbins</i>
                      </p>
                    </Col>
                    <Col xs="12" lg="8" xl="9" className="order-lg-1">
                      <h2 className="section-heading">
                        <i>A Special Message from Ocean Robbins...</i>
                      </h2>
                      <h3 className="mt-0">My Life’s Work Is to Improve Yours</h3>
                      <p className="balance-text">
                        {variant === 'ad' ? (
                          <>I want to share my own family’s painful story. Maybe you can relate.</>
                        ) : (
                          <>I want to share my own family’s painful Alzheimer's story. Maybe you can relate.</>
                        )}
                      </p>
                      <p>
                        My Grandpa Irv founded the renowned “31-flavors” ice cream company. And my dad, John Robbins,
                        grew up with an ice cream cone-shaped swimming pool. He was groomed from early childhood to one
                        day join in running the family company.
                      </p>
                    </Col>
                  </Row>

                  <Row className="d-flex align-items-center justify-content-center">
                    <Col xs="12" sm="8" lg="4" xl="3" className="img-ocean-note-icecream-row text-center">
                      <img
                        className="img-ocean-note img-ocean-note-icecream img-fluid img-rotate-left"
                        src={require('static/irvine-with-ice-cream.jpg')}
                      />
                      <p className="text-small mt-4 mb-0">
                        <i>Irv Robbins</i>
                      </p>
                    </Col>
                    <Col xs="12" lg="8" xl="9">
                      <p>
                        But in his early 20s, <b>my dad walked away from the family business, Baskin-Robbins,</b> and
                        from the immense wealth that could have been his because he didn’t want to spend his life
                        selling a product that could undermine people’s health.
                      </p>
                      <p>
                        <b>
                          I learned early on that you can eat really well and live a very healthy life without being
                          rich (because we definitely weren’t!).
                        </b>
                      </p>
                      <p>
                        <b>
                          {variant === 'ad' ? (
                            <>
                              As a young adult, I watched my paternal grandmother suffer and eventually die after years
                              of cognative decline.
                            </>
                          ) : (
                            <>
                              As a young adult, I watched my paternal grandmother suffer and eventually die of
                              Alzheimer’s disease.
                            </>
                          )}
                        </b>
                      </p>
                    </Col>
                  </Row>
                  <p>I saw firsthand the painful deterioration of her health and cognitive function.</p>
                  <p>By the end of her life, she was blind, incontinent, and couldn’t even remember my name.</p>
                  <p>
                    {variant === 'ad' ? (
                      <>
                        <b>I remember feeling so helpless in the face of such a cruel disease.</b> My heart breaks
                        whenever people share their personal stories with me about the loss of their loved ones this
                        way.
                      </>
                    ) : (
                      <>
                        <b>I remember feeling so helpless in the face of such a cruel disease.</b> My heart breaks
                        whenever people share their personal stories with me about the loss of their loved ones to
                        dementia.
                      </>
                    )}
                  </p>
                  <p>I know how devastating it can be.</p>
                  <p>
                    <b>At the same time, I couldn’t help but wonder:</b> Was all this suffering <i>necessary?</i>
                  </p>
                  <p>
                    Is there any chance that had she adopted a brain-healthy diet and lifestyle, our whole family might
                    have been spared a tragedy?
                  </p>
                  <p>
                    We won’t ever know because my grandma didn’t get a second chance. None of us can rewrite history.
                  </p>
                  <p>But you and I have a choice today.</p>
                  <Row className=" d-flex justify-content-center align-items-center">
                    <Col xs="12" lg="8" className="order-lg-2">
                      <p>Why am I sharing all of this? Well, I want you to know that...</p>
                      <h4 className="mt-4">
                        I’ve spent the last 30+ years dedicated to finding the health answers that our world desperately
                        needs — that maybe YOU need.
                      </h4>
                    </Col>
                    <Col xs="12" sm="8" lg="4" className="img-ocean-note-flexing-row text-center order-lg-1">
                      <img
                        className="img-ocean-note img-ocean-note-flexing img-fluid rounded-circle"
                        src={require('static/speaker/john-ocean-square.jpg')}
                      />
                      <p className="text-small mt-2 mb-0">
                        <i>John & Ocean Robbins</i>
                      </p>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="hbc-team-intro" color="green" angle="both" innerClass="text-white text-center text-shadow">
          <Container>
            <Row>
              <Col>
                <p>
                  {variant === 'ad' ? (
                    <>And when it comes to brain health, I can’t overstate how…</>
                  ) : (
                    <>
                      {
                        {
                          1: <>And when it comes to brain health and dementia, I can’t overstate how…</>,
                          2: <>And when it comes to brain health, I can’t overstate how…</>
                        }[segment]
                      }
                    </>
                  )}
                </p>
                <h2 className="m-0">I’m so excited to introduce Drs. Dean and Ayesha Sherzai (The Brain Docs)</h2>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="hbc-team"
          color="white"
          imgPosition="right"
          imgUrl={require('static/speaker/dean-ayesha-chairs.jpg')}
          imgBgPosition="center-top"
          angle="both"
        >
          <h4 className="section-heading">
            {variant === 'ad' ? (
              <>
                Two neurologists who abandoned their lucrative careers in pharmaceutical research so they could dedicate
                their lives to uncovering a natural and effective way to end cognitive decline.
              </>
            ) : (
              <>
                Two neurologists who abandoned their lucrative careers in pharmaceutical research so they could dedicate
                their lives to uncovering a <i>natural</i> and effective way to end Alzheimer’s.
              </>
            )}
          </h4>
          <p>
            The Brain Docs are a marvelous and unique husband and wife team <b>on the cutting-edge of brain science.</b>
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                <b>Ayesha and Dean</b> met at a small party and fell in love almost immediately. They quickly discovered
                that their grandfathers had known each other. Both of whom were brilliant, kind, wise, hardworking, and
                made a huge impact upon the world.{' '}
                <b>And both of whom had developed brain diseases while their heartbroken families watched.</b>
              </>
            ) : (
              <>
                <b>Ayesha and Dean</b> met at a small party and fell in love almost immediately. They quickly discovered
                that their grandfathers had known each other. Both of whom were brilliant, kind, wise, hardworking, and
                made a huge impact upon the world.{' '}
                <b>And both of whom had developed dementia while their heartbroken families watched.</b>
              </>
            )}
          </p>
          <p>
            With backgrounds at Columbia University and prestigious research institutes like National Institute of
            Health (NIH), the Sherzai’s could have continued their work as rising stars in Big Pharma.
          </p>
        </SectionImage>

        <SectionImage
          id="hbc-spoiler"
          color="light-gray"
          imgPosition="left"
          imgUrl={require('static/backgrounds/lightbulb-square.jpg')}
          imgBgPosition="center-bottom"
        >
          <h4 className="section-heading">
            {variant === 'ad' ? (
              <>
                They walked away from their high-powered careers because they wanted to find a solution for the brain
                health epidemic.
              </>
            ) : (
              <>
                They walked away from their high-powered careers because hey wanted to find a solution for the
                Alzheimer’s epidemic.
              </>
            )}
          </h4>
          <h4 className="mt-0">Spoiler alert: they did.</h4>
          <p>But they had to leave their lucrative jobs first.</p>
          <p>
            {variant === 'ad' ? (
              <>
                You see, despite the best efforts of thousands of hardworking scientists around the world,{' '}
                <b>
                  <i>there still isn’t an effective pharmaceutical treatment for cognitive decline.</i>
                </b>
              </>
            ) : (
              <>
                You see, despite the best efforts of thousands of hardworking scientists around the world,{' '}
                <b>
                  <i>there still isn’t an effective pharmaceutical treatment for dementia.</i>
                </b>
              </>
            )}
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                But thankfully, The Brain Docs got to work developing the only scientifically proven plan to fight
                cognitive decline before it even starts…
              </>
            ) : (
              <>
                But thankfully, The Brain Docs got to work developing the only scientifically proven plan to fight
                Alzheimer’s before it even starts…
              </>
            )}
          </p>
          <p>
            <b>
              <i>All it takes is the right food and a few easy-to-adopt habits.</i>
            </b>
          </p>
          <p>
            {variant === 'ad' ? (
              <>The Brain Docs have discovered a proven method for giving millions of people a beautiful future.</>
            ) : (
              <>
                The Brain Docs have discovered a proven method for slowing or even sometimes reversing symptoms of
                cognitive decline, dementia, and giving millions of people a beautiful future.
              </>
            )}
          </p>
          <h5 className="mt-4">
            {variant === 'ad' ? (
              <>
                These same principles are also shown to help boost brain power, memory, and productivity — while
                increasing deep sleep and energy.
              </>
            ) : (
              <>
                These same discoveries are also shown to help boost brain power, memory, and productivity — while
                increasing deep sleep and energy in as little as a few weeks.
              </>
            )}
          </h5>
        </SectionImage>

        <Section id="" color="white">
          <Container>
            <Row>
              <Col>
                <TestimonialBubble id={variant === 'ad' ? 'neil-s-ad' : 'neil-s-v1'} className="mb-5" />
                <TestimonialBubble id="sharon-p-v1" className="mb-5" />
                <TestimonialBubble id="lynette-p-v1" className="mb-5" />
                <TestimonialBubble id="ruth-g-v1" />
                <p className="text-small text-center balance-text mt-5">
                  <i>
                    These testimonials reflect people’s unique experience with The Brain Docs’ work. Your specific
                    experience will vary.
                  </i>
                </p>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="" color="light-gray">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h3 className="section-heading text-center text-400">
                  Discover the astonishing breakthroughs in the latest brain health science so you can{' '}
                  <b>
                    <i>make the most of your life.</i>
                  </b>
                </h3>
                <IconList>
                  <IconListItem>
                    6 modules, each with 7 video lessons — all available in HD video, MP3 audio, and downloadable
                    transcript format
                  </IconListItem>
                  <IconListItem>Access to an Exclusive Forum (that’s NOT on Facebook)</IconListItem>
                  <IconListItem>
                    100% plant-based, non-GMO recipes that are super easy to implement — with gluten-free and soy-free
                    options
                  </IconListItem>
                  <IconListItem>
                    6 delightful recipe preparation videos with neuroscientist and plant-based chef Dr. Ayesha Sherzai
                    (see exactly <i>how</i> she does it!)
                  </IconListItem>
                  <IconListItem>
                    11 workbooks and bonus resources to help you track your progress and implement everything you learn
                    in the course.
                  </IconListItem>
                  <IconListItem>
                    Q&A with Dean and Ayesha Sherzai, MD, hosted by Ocean Robbins – where we answer the TOP questions
                    from our community (pre-recorded, transcribed, and super easy to navigate so you can get the answers
                    YOU want).
                  </IconListItem>
                </IconList>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="" color="white">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <h5 className="section-heading">Video lessons that are easy to follow and simple to access</h5>
                <p>
                  You pick how you learn best! Each lesson is available in HD video, MP3 audio, and downloadable
                  transcript format. Simply press play and watch the videos, download the audios to listen on-the-go, or
                  grab the transcript for a riveting morning read.
                </p>
                <h5 className="mt-4">Cooking demos and recipes to mend & maximize brain power</h5>
                <p>
                  What’s the best way to learn something new and make sure you remember it? By watching someone show you
                  how! Join neuroscientist and plant-based chef Dr. Ayesha Sherzai as she shows you <i>exactly</i> how
                  to prepare brain-healthy meals like Portobello Mushroom Pizza and Cashew Parmesan — easily,
                  affordably, and scrumptiously.
                </p>
                <p>
                  If you’re like us, these 100% plant-based, non-GMO recipes will become your new weeknight favorites
                  (Gluten-free and soy-free? We’ve got you covered.)
                </p>
                <h5 className="mt-4">More than a course — it’s a community and ongoing support!</h5>
                <p>
                  As soon as you join, you’ll be welcomed into our private online community forum, where you can connect
                  with peers, share struggles and successes.
                </p>
                <p>
                  You’ll love being able to get support from our Team on your brain health questions. Come back any time
                  you have a new question or concern!
                </p>
                <p>(And by the way, this forum is NOT on Facebook.)</p>
                <h5 className="mt-4">
                  11 workbooks and bonus resources to help you put everything you learn <i>to use.</i>
                </h5>
                <p>
                  Only in the last fifteen years have we mapped out brain regions and uncovered incredible insights into
                  how the brain really works. But knowledge without action won’t get results — which is why everything
                  in this course is brilliantly designed to help you USE what you learn.
                </p>
                <p>
                  (And yes, we employed the latest brain science to develop these workbooks, making them not just full
                  of useful tools... but conducive to the way your mind loves to learn.)
                </p>
                <h5 className="mt-4">Bonus Q&A with Dean and Ayesha Sherzai, MD, and Ocean Robbins </h5>
                <p>
                  We answer the TOP questions from our community (pre-recorded and transcribed so you can get the
                  answers YOU want).
                </p>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="join-leap"
          color="light-gray"
          imgPosition="right"
          imgUrl={require('static/backgrounds/model-brain.jpg')}
          title={
            <>
              <h2 className="section-heading">
                In the last 15 years, brain health science has experienced a quantum leap.
              </h2>
              <h3 className="mt-0 text-400">
                Unless you’re a neurologist, you might have missed these breakthroughs… but it’s far too important to
                skip.
              </h3>
              <h3 className="m-0 text-400">Are you ready to finally give your brain what it needs?</h3>
            </>
          }
          titleClass="mb-0 text-lg-center"
        >
          <Container>
            <Row>
              <Col>
                <h4>
                  {variant === 'ad' ? (
                    <>
                      With <i>Healthy Brain,</i> The Brain Docs empowers us to…
                    </>
                  ) : (
                    <>
                      With <i>Healthy Brain,</i> The Brain Docs will empower you to…
                    </>
                  )}
                </h4>
                <IconList>
                  <IconListItem>
                    Get optimal results through stunningly <b>simple, doable actions</b>
                  </IconListItem>
                  <IconListItem>
                    <b>Prepare brain-healthy meals</b> — easily, affordably, and deliciously
                  </IconListItem>
                  <IconListItem>
                    Utilize <b>proven habit change hacks</b> to consistently achieve your goals
                  </IconListItem>
                  <IconListItem>
                    Make it <b>easy</b> to live a <b>brain-healthy life</b> — so you can get the results you want
                  </IconListItem>
                  <IconListItem>
                    {variant === 'ad' ? (
                      <>
                        Enjoy a{' '}
                        <b>lifestyle that’s been proven to keep brains healthy — and keep you healthy overall</b>
                      </>
                    ) : (
                      <>
                        Enjoy the <b>lifestyle that’s been proven to reduce your risk of Alzheimer’s disease</b> — and
                        that can ALSO help you lose any excess weight while lowering your risk of heart disease, type 2
                        diabetes, cancer, arthritis, osteoporosis, and many other ailments
                      </>
                    )}
                  </IconListItem>
                </IconList>
              </Col>
            </Row>
          </Container>
        </SectionImage>

        <Section id="" color="white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-uppercase">
                  The <i>Healthy Brain</i> Curriculum
                </h3>
              </Col>
            </Row>
            <Row>
              <Col>{variant === 'ad' ? <ModuleAccordionV1 modulelist={modulesAd} /> : <ModuleAccordionV1 />}</Col>
            </Row>
            <Row>
              <Col>
                <h3>
                  <i>Healthy Brain</i> could be the most important course you ever take.
                </h3>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="" color="light-gray">
          <Container>
            <Row>
              <Col>
                <TestimonialBubble id="elisa-f-v1" bgColor="white" className="mb-5" />
                <TestimonialBubble id="steve-f-v1" bgColor="white" className="mb-5" />
                <TestimonialBubble id="paul-m-v1" bgColor="white" className="mb-5" />
                <TestimonialBubble id="mo-s-v1" bgColor="white" />
                <p className="text-small text-center mt-5">
                  <i>
                    These testimonials reflect people’s unique experience with The Brain Docs’ work. Your specific
                    experience will vary.
                  </i>
                </p>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="" color="gradient-indigo-purple-right">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center text-white">
                  {variant === 'ad' ? (
                    <>The world’s top brain health experts want you to be in the know…</>
                  ) : (
                    <>Uncover what the world’s top brain health experts want you to know…</>
                  )}
                </h3>
                <h3 className="mt-0 mb-5 text-400 text-center text-white">
                  {variant === 'ad' ? (
                    <>
                      It really IS possible to think more clearly now, add <i>years</i> (or even decades) of peak brain
                      performance and keep your brain healthy!
                    </>
                  ) : (
                    <>
                      So you can think more clearly now, add <i>years</i> of peak brain performance onto your life, and
                      slash your risk of Alzheimer’s disease and other dementias!
                    </>
                  )}
                </h3>
                <SalesCta
                  headline={
                    <h2 className="section-heading">
                      {variant === 'ad' ? (
                        <>Get The Brain Health Facts You Need NOW</>
                      ) : (
                        <>Boost Your Brain Health NOW</>
                      )}
                    </h2>
                  }
                />
              </Col>
            </Row>
          </Container>
        </Section>

        {variant === 'ad' ? null : (
          <Section id="hbc-testimonials" color="white" angleWrapClass="d-none">
            <Container>
              <Row>
                <Col>
                  <TestimonialBubble id="liz-w" className="mb-5" />
                  <TestimonialBubble id="claire-b" />
                </Col>
              </Row>
            </Container>
          </Section>
        )}

        <Section id="hbc-bonuses" color="white" innerClass={variant === 'ad' ? null : 'pt-5'}>
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">
                  And There’s Even More! Enroll Now for These Powerful Bonuses (Worth over $300)
                </h2>
                <p>
                  When you join <i>Healthy Brain</i> today, you’ll gain access to a powerful collection of free bonuses
                  from our friends and partners.
                </p>
                <BonusAccordion />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="" color="gradient-indigo-purple-right">
          <Container>
            <Row>
              <Col>
                <SalesCta headline={<h2 className="section-heading">Take Charge of Your Brain Health</h2>} />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="" color="light-gray">
          <Container>
            <Row>
              <Col>
                <TestimonialBubble
                  id={variant === 'ad' ? 'hbc-sharon-r-ad' : 'hbc-sharon-r'}
                  bgColor="white"
                  className="mb-5"
                />
                <TestimonialBubble
                  id={variant === 'ad' ? 'hbc-jennifer-b-ad' : 'hbc-jennifer-b'}
                  bgColor="white"
                  className="mb-5"
                />
                <TestimonialBubble id="hbc-kathleen" bgColor="white" className="mb-5" />
                <TestimonialBubble
                  id={variant === 'ad' ? 'hbc-janet-r-ad' : 'hbc-janet-r'}
                  bgColor="white"
                  className="mb-5"
                />
                <TestimonialBubble
                  id={variant === 'ad' ? 'hbc-donna-b-ad' : 'hbc-donna-b'}
                  bgColor="white"
                  className="mb-5"
                />
                <TestimonialBubble id="hbc-maureen" bgColor="white" className="mb-5" />
                <p className="text-small text-center mt-5">
                  <i>
                    These stories are from people who’ve found success with our programs. Each of them is unique. And so
                    are you! Your experiences will vary, of course. And no, we never offer compensation for these
                    statements!
                  </i>
                </p>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="hbc-costs"
          color="white"
          imgPosition="right"
          imgUrl={require('static/backgrounds/woman-wheelchair-square.jpg')}
        >
          <h2 className="section-heading">
            {variant === 'ad' ? <>What’s the cost of doing nothing?</> : <>Dementia costs, big time.</>}
          </h2>
          <p>
            {variant === 'ad' ? (
              <>
                In the next 15 years, if we don’t change course, cognitive decline will overwhelm not just our health
                care system, but society as we know it. The emotional cost of the disease is immeasurable, and the
                financial cost is absolutely overwhelming.
              </>
            ) : (
              <>
                In the next 15 years, if we don’t change course, dementia will overwhelm not just our health care
                system, but society as we know it. The emotional cost of the disease is immeasurable, and the financial
                cost is absolutely overwhelming.
              </>
            )}
          </p>
          <p>This trillion-dollar disease could collapse our already overburdened health care system.</p>
          <p>
            <b>And even more importantly, it will overwhelm our families and communities.</b>
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Every family right now has a connection with somebody suffering from this disease. The patient suffers
                the loss of their memory, their impulse control, and their very identity. And the family members tasked
                with caring for them may suffer even more.
              </>
            ) : (
              <>
                Every family right now has a connection with somebody suffering from this disease. The person with
                Alzheimer's suffers the loss of their memory, their impulse control, and their very identity. And the
                family members tasked with caring for them may suffer even more.
              </>
            )}
          </p>
        </SectionImage>

        <SectionImage
          id="hbc-priceless"
          color="light-gray"
          imgPosition="left"
          imgUrl={require('static/backgrounds/man-boy-square.jpg')}
        >
          <h3 className="section-heading">The truth is, your brain is priceless.</h3>
          <p>
            <b>And the time to take action is now.</b>
          </p>
          <p>
            {variant === 'ad' ? (
              <>
                Drs. Dean and Ayesha have spent over 15 years researching the truth and helping thousands of their
                patients to reclaim their brains and their lives.
              </>
            ) : (
              <>
                Drs. Dean and Ayesha have spent over 15 years researching the truth about dementia and helping thousands
                of their patients to reclaim their brains and their lives.
              </>
            )}
          </p>
          <p>
            Now they’ve distilled their hard-won research and turned it into a brilliant, never-look-back program that
            can finally put your brain on the right path.
          </p>
          <p>
            <b>What’s that worth?</b>
          </p>
          <p>
            What’s it worth to have your mental health, clarity, memories, and the independence that make it easy for
            you to live your life on your terms, connected with your loved ones for decades to come?
          </p>
          <p>
            <b>
              <i>Everyone</i> needs this information.
            </b>
          </p>
          <p>
            That’s why The Brain Docs designed <i>Healthy Brain</i> so that it doesn’t depend on fancy supplements or
            gadgets that cost an arm and a leg.
          </p>
          <p>
            In fact, you’ll be pleased to know that most of what you need to protect and enhance your brain health is
            free!
          </p>
        </SectionImage>

        <Section id="" color="gradient-indigo-purple-right">
          <Container>
            <Row>
              <Col>
                <SalesCta
                  headline={
                    <h2 className="section-heading">
                      <i>Healthy Brain</i> could be the most important course you ever take.
                    </h2>
                  }
                />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="hbc-guarantee" color="light-gray">
          <Container>
            <Row>
              <Col>
                <Guarantee />
              </Col>
            </Row>
            <Row className="mt-5 mt-sm-0">
              <Col>
                <TestimonialBubble id="meg-h" bgColor="white" />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="faq" color="white">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">Frequently Asked Questions</h2>
                {variant === 'ad' ? <FaqListV1 faqlist={faqAd} /> : <FaqListV1 />}
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="testimonials" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-5">Lives Changed By The Brain Docs’ Work</h2>
                <TestimonialBubble id="anna-c" bgColor="white" className="mb-5" />
                <TestimonialBubble id="barbara-d" bgColor="white" className="mb-5" />
                <TestimonialBubble
                  id={variant === 'ad' ? 'alapaki-j-ad' : 'alapaki-j'}
                  bgColor="white"
                  className="mb-5"
                />
                <TestimonialBubble id="doreen-s" bgColor="white" className="mb-5" />
                <TestimonialBubble id="aly-s" bgColor="white" />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="hbc-join-cta-5" color="gradient-indigo-purple-right">
          <Container>
            <Row>
              <Col>
                <SalesCta headline={<h2 className="section-heading">Join Now!</h2>} />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="references" className="section-angle-last" color="light-gray" angle="none">
          <Container>
            <Row className="d-flex justify-content-center">
              <Col>
                <h3 className="section-heading">References</h3>

                <ul className="list-references list-group text-small mb-0">
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[1]</div>
                    <div>
                      “Dementia”. World Health Organization. 19 Sept 2019.
                      <br />
                      Available from: https://www.who.int/news-room/fact-sheets/detail/dementia
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[2]</div>
                    <div>
                      “Alzheimer’s Disease Fact Sheet.” NIH. National Institute on Aging. Reviewed 22 May 2019.
                      <br />
                      Available from: https://www.nia.nih.gov/health/alzheimers-disease-fact-sheet
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[3]</div>
                    <div>
                      2020 Alzheimer's disease facts and figures [published online ahead of print, 2020 Mar 10].
                      <br />
                      Alzheimers Dement. 2020;10.1002/alz.12068. doi:10.1002/alz.12068
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[4]</div>
                    <div>
                      Alzheimer’s Association. 2019 Alzheimer’s Disease Facts and Figures. Alzheimers Dement.
                      2019;15(3):321-87.
                      <br />
                      Available from: https://www.alz.org/media/Documents/alzheimers-facts-and-figures-2019-r.pdf
                    </div>
                  </li>
                  <li className="list-group-item reference-item d-flex align-items-center p-3">
                    <div className="pr-3">[5]</div>
                    <div>
                      “Dementia cases set to triple by 2050 but still largely ignored.” World Health Organization. 11
                      April 2012.
                      <br />
                      Available from: https://www.who.int/mediacentre/news/releases/2012/dementia_20120411/en/
                    </div>
                  </li>
                </ul>
              </Col>
            </Row>
          </Container>
        </Section>
      </div>
      {!noExitIntent && (
        <ExitModal
          exitDelay={60000}
          content={
            <>
              {!salesFullPrice ? (
                <>
                  <h4 className="section-heading">
                    Don't miss out! <br className="d-none d-lg-block d-xl-none" />
                    You'll only get <br className="d-none d-sm-block d-lg-none" />
                    this special offer here.
                  </h4>
                  <p>
                    Get <i>Healthy Brain,</i> the <br className="d-none d-lg-block d-xl-none" />
                    life-changing course, <br className="d-none d-sm-block d-xl-none" />
                    for only ${productValue} — <b>a $200 savings.</b>
                  </p>
                </>
              ) : (
                <>
                  <h4 className="section-heading">
                    Your brain cares about <br className="d-none d-sm-block" />
                    your next move.
                  </h4>
                  <h5 className="mt-0">
                    The incredible potential of your <br className="d-none d-sm-block d-lg-none d-xl-block" />
                    brain lies in your hands.
                  </h5>
                  <p>
                    Invest in your brain health and own <i>Healthy Brain,</i>{' '}
                    <br className="d-none d-sm-block d-lg-none" />
                    the life-changing course, for just ${productFullPrice}.
                  </p>
                </>
              )}
            </>
          }
          image={{
            url: !salesFullPrice
              ? require('static/backgrounds/man-tablet.jpg')
              : require('static/backgrounds/couple-couch.jpg'),
            alt: '',
            width: 1000,
            height: 800
          }}
          positiveButtonText={!salesFullPrice ? <>Save money AND invest in your brain TODAY</> : 'Get it Now'}
          negativeButtonText="No Thanks"
          scrollHref="hbc-join-cta-5"
          scrollOffset={0}
        />
      )}
      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
